import { PhxTag } from 'phx-core';

import { IMoment, moment } from '@lib/moment';

import { IBreadcrumb } from '@misc/page-header/page-header.service';

import { COACHING_STATUSES, IDriveScoreContributorLabels } from '@lib/safety/safety';
import { CameraPosition } from '@lib/cam-types';

export enum ImageSrcType {
  Jpg = 'image/jpg',
}

export enum VideoSrcType {
  Mp4 = 'video/mp4',
  Mp4Tar = 'video_mp4/tar',
  WebM = 'video/webm',
}

export type MediaSrcType = ImageSrcType | VideoSrcType;

export const SAFETY_SVC_BASE_URL: string = 'safety/v2';

export const SAFETY_ROOT_BREADCRUMB: IBreadcrumb = [{ title: $localize`Safety` }];

export const DRIVE_SCORE_WEEKS_DURATION: number = 4;

export const OBFUSCATED_PHONE_NUMBER: string = '+12345678910';

export const PREVIOUS_MOST_DATE: IMoment = moment().subtract(12, 'weeks');

export const DRIVE_SCORE_CONTRIBUTOR_LABELS: IDriveScoreContributorLabels = {
  hard_brake: $localize`braking`,
  hard_accel: $localize`accelerating`,
  hard_corner: $localize`cornering`,
  speeding: $localize`speeding`,
  hos_violation: $localize`HOS violations`,
};

interface IEnvironmentalConditions {
  high_traffic_density: string;
  medium_traffic_density: string;
  high_tpv_behavior: string;
  medium_tpv_behavior: string;
  yes_sensitive_zone: string;
  pedestrians_traffic_conditions: string;
  animal_traffic_conditions: string;
  bicyclists_traffic_conditions: string;
  police_traffic_conditions: string;
  debris_road_conditions: string;
  snow_road_conditions: string;
  ice_road_conditions: string;
  wet_road_conditions: string;
  oil_road_conditions: string;
  not_visible_road_conditions: string;
  slightly_curved_road_geometry: string;
  very_curved_road_geometry: string;
  uphill_slope: string;
  downhill_slope: string;
  constricted_lane_spacing: string;
  not_visible_road_visibility: string;
  slightly_visible_road_visibility: string;
  glare_driving_visibility: string;
  rain_driving_visibility: string;
  snow_driving_visibility: string;
  fog_driving_visibility: string;
  not_visible_driving_visibility: string;
  wind_driving_visibility: string;
  twilight_lighting: string;
  night_lighting: string;
  highway_road_type: string;
  city_road_type: string;
  parking_lot_road_type: string;
  off_road_road_type: string;
  yes_traffic_violations: string;
  slower_vehicle_speed: string;
  faster_vehicle_speed: string;
  sensitive_zone: string;
  traffic_violations: string;
}

export type EnvironmentalConditionsType = keyof IEnvironmentalConditions;

export const ENVIRONMENTAL_CONDITIONS: IEnvironmentalConditions = {
  high_traffic_density: $localize`Heavy Traffic Zone`,
  medium_traffic_density: $localize`Light Traffic Zone`,
  high_tpv_behavior: $localize`Very Risky Driver Nearby`,
  medium_tpv_behavior: $localize`Risky Driver Nearby`,
  yes_sensitive_zone: $localize`Sensitive Zone`,
  pedestrians_traffic_conditions: $localize`Pedestrians Nearby`,
  animal_traffic_conditions: $localize`Animals Nearby`,
  bicyclists_traffic_conditions: $localize`Cyclists Nearby`,
  police_traffic_conditions: $localize`Police Nearby`,
  debris_road_conditions: $localize`Road Debris`,
  snow_road_conditions: $localize`Snow On Roadway`,
  ice_road_conditions: $localize`Icy Roadway`,
  wet_road_conditions: $localize`Wet Roadway`,
  oil_road_conditions: $localize`Oil On Roadway`,
  not_visible_road_conditions: $localize`No Visibility`,
  slightly_curved_road_geometry: $localize`Curved Roadway`,
  very_curved_road_geometry: $localize`Winding Road`,
  uphill_slope: $localize`Uphill Slope`,
  downhill_slope: $localize`Downhill Slope`,
  constricted_lane_spacing: $localize`Narrow Lane`,
  not_visible_road_visibility: $localize`No Lane Markings`,
  slightly_visible_road_visibility: $localize`Unclear Lane Markings`,
  glare_driving_visibility: $localize`Intense Glare`,
  rain_driving_visibility: $localize`Rainy Weather`,
  snow_driving_visibility: $localize`Snowy Weather`,
  fog_driving_visibility: $localize`Foggy Weather`,
  not_visible_driving_visibility: $localize`No Visibility`,
  wind_driving_visibility: $localize`Poor Visibility`,
  twilight_lighting: $localize`Dawn/Dusk`,
  night_lighting: $localize`Nighttime`,
  highway_road_type: $localize`Highway`,
  city_road_type: $localize`Local Road`,
  parking_lot_road_type: $localize`Parking Lot`,
  off_road_road_type: $localize`Off Road`,
  yes_traffic_violations: $localize`Traffic Violation`,
  slower_vehicle_speed: $localize`Slower Than Traffic`,
  faster_vehicle_speed: $localize`Faster Than Traffic`,
  sensitive_zone: $localize`Sensitive Zone`,
  traffic_violations: $localize`Traffic Violation`,
};

export const ENVIRONMENTAL_CONDITION_DEFINITIONS: IEnvironmentalConditions = {
  high_traffic_density: $localize`Higher than usual traffic density`,
  medium_traffic_density: $localize`Lower than usual traffic density`,
  high_tpv_behavior: $localize`One or more nearby drivers were driving in an unsafe manner.`,
  medium_tpv_behavior: $localize`One or more nearby drivers were driving in an unsafe manner.`,
  yes_sensitive_zone: $localize`Driving near schools, hospitals, bridges, construction or residential areas. Includes slowdowns caused by other roadside incidents.`,
  pedestrians_traffic_conditions: $localize`One or more pedestrians were near this driver’s vehicle.`,
  animal_traffic_conditions: $localize`One or more animals were near this driver’s vehicle.`,
  bicyclists_traffic_conditions: $localize`One or more cyclists were near this driver’s vehicle.`,
  police_traffic_conditions: $localize`Police vehicle or officer was near this driver’s vehicle.`,
  debris_road_conditions: $localize`Objects that pose a risk to drivers were visible on the road, like tires or roadkill.`,
  snow_road_conditions: $localize`Snow was visible on the road.`,
  ice_road_conditions: $localize`Ice was visible on the road.`,
  wet_road_conditions: $localize`Standing water was visible on the road.`,
  oil_road_conditions: $localize`Spilled oil was visible on the road.`,
  not_visible_road_conditions: $localize`Road conditions were not visible due to improper dashcam installation.`,
  slightly_curved_road_geometry: $localize`Driving through one or more mild turns`,
  very_curved_road_geometry: $localize`Driving through one or more sharp turns`,
  uphill_slope: $localize`Road surface is sloping upwards.`,
  downhill_slope: $localize`Road surface is sloping downwards.`,
  constricted_lane_spacing: $localize`Constricted or decreasing lane width`,
  not_visible_road_visibility: $localize`Lane markings were not visible due to weather conditions or improper dashcam installation.`,
  slightly_visible_road_visibility: $localize`Lane markings were partially visible due to weather conditions or improper dashcam installation.`,
  glare_driving_visibility: $localize`Reduced visibility due to direct sunlight`,
  rain_driving_visibility: $localize`Reduced visibility due to rain`,
  snow_driving_visibility: $localize`Reduced visibility due to snow`,
  fog_driving_visibility: $localize`Reduced visibility due to fog`,
  not_visible_driving_visibility: $localize`Road conditions were not visible due to improper dashcam installation.`,
  wind_driving_visibility: $localize`Reduced visibility due to general weather conditions`,
  twilight_lighting: $localize`Reduced visibility due to twilight conditions`,
  night_lighting: $localize`Reduced visibility at night`,
  highway_road_type: $localize`Driving on a highway or freeway`,
  city_road_type: $localize`Driving near populated areas`,
  parking_lot_road_type: $localize`Driving within a parking lot`,
  off_road_road_type: $localize`Driving on unsurfaced roads`,
  yes_traffic_violations: $localize`Includes rolling stops, overspeeding, running a red light, extreme driving, or other illegal actions based on visible road signs`,
  slower_vehicle_speed: $localize`Driving slower than the free flow of traffic`,
  faster_vehicle_speed: $localize`Driving faster than the free flow of traffic`,
  sensitive_zone: $localize`Sensitive Zone`,
  traffic_violations: $localize`Traffic Violation`,
};

export const AUTOMATED_COACHING_REASONS: { [key: string]: string } = {
  tailgating: $localize`This event is coachable because you were tailgating.`,
  unsafe_lane_change: $localize`This event is coachable because you changed lanes unsafely.`,
  cell_phone: $localize`This event is coachable because you used your cell phone while driving.`,
  distraction: $localize`This event is coachable because you appeared distracted while driving.`,
  drowsiness: $localize`This event is coachable because you appeared drowsy while driving.`,
  seat_belt_violation: $localize`This event is coachable because you weren\'t wearing your seat belt.`,
  yes_traffic_violations: $localize`This event is coachable because you committed a traffic violation.`,
  faster_vehicle_speed: $localize`This event is coachable because you were speeding.`,
};

export const CAMERA_POSITION_TO_LABEL: Record<CameraPosition, string> = {
  [CameraPosition.Road]: $localize`Road`,
  [CameraPosition.Driver]: $localize`Driver`,
  [CameraPosition.Left]: $localize`Left`,
  [CameraPosition.Right]: $localize`Right`,
  [CameraPosition.Rear]: $localize`Rear`,
  [CameraPosition.LicensePlate]: 'Road - Zoom',
};

export enum REQUEST_STATUS_TYPES {
  SUCCESS = 'success',
  FAILURE = 'failure',
  PENDING = 'pending',
}

export enum VIDEO_FAILED_REASONS_KEYS {
  video_not_uploaded = 'video_not_uploaded',
  video_not_found = 'video_not_found',
  image_request_timed_out = 'image_request_timed_out',
}

export const KPH_THRESHOLD: number = 4.8;
export const MPH_THRESHOLD: number = 3;

export const VISIBILITY_REQUEST_PARAM: string = 'confidential';

export const COACHING_STATUS_TAG_CONFIG: Partial<Record<COACHING_STATUSES, Partial<PhxTag>>> = {
  pending_review: { tagType: 'caution', contrast: 'low' },
  coachable: { tagType: 'negative', contrast: 'low' },
  coached: { tagType: 'neutral', contrast: 'low' },
  uncoachable: { tagType: 'neutral', contrast: 'low' },
};

export const NO_RESULTS_PHX_ICON_SIZE: number = 64;